import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { cn } from 'src/utils/cx'
import {
  GALLERY_IMAGES,
  GALLERY_TAB_KEYS,
  PREMIUM_GALLERY_INDICES,
} from '../constants'
import { useNavigateToGenerator } from '../hooks/useNavigateToGenerator'
import css from '../Landing.module.css'
import { useFadeUp } from './useFadeUp'

export default function GallerySection() {
  const { t } = useTranslation('blackboard', { keyPrefix: 'welcome' })
  const [activeTab, setActiveTab] = useState(0)
  const fadeRef = useFadeUp()
  const navigateToGenerator = useNavigateToGenerator()

  const filteredImages = useMemo(() => {
    const tabKey = GALLERY_TAB_KEYS[activeTab]
    if (tabKey === 'all')
      return GALLERY_IMAGES.map((img, i) => ({ ...img, originalIndex: i }))
    return GALLERY_IMAGES.map((img, i) => ({
      ...img,
      originalIndex: i,
    })).filter(img => img.category === tabKey)
  }, [activeTab])

  return (
    <section id="gallery" className="py-24">
      <div className="mx-auto max-w-[1200px] px-6">
        <div ref={fadeRef} className={cn(css.fadeUp)}>
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="mb-2 text-sm font-bold uppercase tracking-wider text-[--landing-pink]">
                {t('gallery.section-label')}
              </div>
              <h2 className="mb-4 text-4xl font-semibold leading-tight text-gray-900">
                <Trans
                  i18nKey="blackboard:welcome.gallery.heading"
                  components={{
                    highlight: <span className={css.gradientText} />,
                  }}
                />
              </h2>
              <p className="max-w-[600px] text-base text-gray-500">
                {t('gallery.subheading')}
              </p>
            </div>
          </div>

          <div className="mb-6 flex flex-wrap gap-2">
            {GALLERY_TAB_KEYS.map((key, i) => (
              <button
                key={key}
                onClick={() => setActiveTab(i)}
                className={cn(
                  'rounded-full border-[1.5px] px-5 py-2 text-sm font-medium transition-all',
                  activeTab === i
                    ? 'border-[--landing-pink] bg-[--landing-pink-bg] text-[--landing-pink-dark]'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-[--landing-pink] hover:bg-[--landing-pink-bg] hover:text-[--landing-pink-dark]',
                )}
              >
                {t(`gallery.tabs.${key}`)}
              </button>
            ))}
          </div>

          <div
            className="landing-gallery-grid gap-4"
            style={{
              columns: 'var(--gallery-cols, 4)',
              columnGap: '16px',
            }}
          >
            <style>{`
              @media (max-width: 1024px) { .landing-gallery-grid { --gallery-cols: 3; } }
              @media (max-width: 768px) { .landing-gallery-grid { --gallery-cols: 2; } }
              @media (min-width: 1025px) { .landing-gallery-grid { --gallery-cols: 4; } }
            `}</style>
            {filteredImages.map(img => (
              <div
                key={img.originalIndex}
                className="group relative mb-4 cursor-pointer overflow-hidden rounded-2xl transition-transform hover:-translate-y-1"
                style={{ breakInside: 'avoid' }}
              >
                {PREMIUM_GALLERY_INDICES.includes(img.originalIndex) && (
                  <span className="absolute right-2.5 top-2.5 z-[2] rounded-lg bg-gradient-to-br from-[#FF83B8] to-[#FF5A9E] px-2.5 py-1 text-[11px] font-bold text-white shadow-[0_2px_8px_rgba(255,131,184,0.4)]">
                    {t('gallery.premium-badge')}
                  </span>
                )}
                <img
                  src={img.src}
                  alt={t(`gallery.images.${img.originalIndex}`)}
                  loading="lazy"
                  className="block w-full"
                />
                <button
                  type="button"
                  className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/60 via-transparent to-transparent p-4 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--landing-pink]"
                  onClick={() => navigateToGenerator()}
                >
                  <span className="text-sm font-semibold text-white">
                    {t('gallery.create-with-style')}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
